import { useCallback, useState } from "react";
import type {
  IAiScriptEditedCellSelection,
  IAiSpreadsheetCellOverride,
} from "@/payroll/ai-script/types";

type ManualEditsOpenSource = "cell" | "header";
type ManualEditsSidebarState = {
  openSource: ManualEditsOpenSource | null;
  focusedEdit: IAiScriptEditedCellSelection | null;
};

const CLOSED_STATE: ManualEditsSidebarState = {
  openSource: null,
  focusedEdit: null,
};

export const useManualEditsSidebar = () => {
  const [state, setState] = useState<ManualEditsSidebarState>(CLOSED_STATE);

  const close = useCallback(() => {
    setState(CLOSED_STATE);
  }, []);

  const openList = useCallback(() => {
    setState({ openSource: "header", focusedEdit: null });
  }, []);

  const openEdit = useCallback((selection: IAiScriptEditedCellSelection) => {
    setState({ openSource: "cell", focusedEdit: selection });
  }, []);

  const focusEdit = useCallback((override: IAiSpreadsheetCellOverride) => {
    setState((current) => ({
      ...current,
      focusedEdit: { override, coordinate: override.cell?.address ?? "—" },
    }));
  }, []);

  // Returning to the full list makes a cell-opened sidebar sticky instead of
  // tying its lifetime to the selected spreadsheet cell.
  const showAll = useCallback(() => {
    setState((current) => ({
      openSource: current.openSource === "cell" ? "header" : current.openSource,
      focusedEdit: null,
    }));
  }, []);

  const handleSelectionChange = useCallback(
    (selection: IAiScriptEditedCellSelection | null) =>
      setState((current) => {
        if (current.openSource !== "cell") return current;

        // Clicking off to a non-edited cell (or clearing the selection) closes the
        // cell-opened sidebar. Clicking another edited cell keeps it open and
        // re-points it at that cell's edit.
        if (!selection) return CLOSED_STATE;

        return current.focusedEdit?.override.id === selection.override.id
          ? current
          : { ...current, focusedEdit: selection };
      }),
    [],
  );

  return {
    isOpen: state.openSource !== null,
    focusedEdit: state.focusedEdit,
    close,
    openList,
    openEdit,
    focusEdit,
    showAll,
    handleSelectionChange,
  };
};
